import { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";

// import { ReactComponent as CreditWarning } from "assets/images/icons/creditWarning.svg";
import { ReactComponent as CreditWarning } from "assets/images/icons/credit-warning.svg";
import { ButtonLoader, SelectInput, Input } from "components/__new/common/form";
import Maybe from "components/__new/common/Maybe";
import FileUploadUI from "components/__new/common/form/FileUploadUI";
import { FileUploader } from "react-drag-drop-files";
import getFileSize from "utils/getFileSize";
import iconCSV from "assets/images/icons/uploadcloud.svg";
import { ReactComponent as RedXIcon } from "assets/images/icons/x.svg";
import useLoginInfo from "hooks/useLoginInfo";
import * as walletActions from 'store/entities/wallet/action';
import * as banksActions from "store/ui/banks/action";
import { CreditStatus } from "./CreditInfo";
import ReactSelect from 'react-select';
import { reactSelectStyles } from 'utils/reactSelectStyles';
import useBanks from "hooks/useBanks";

const CreditApplication = () => {
    const dispatch = useDispatch();
    const history = useHistory()
    const { userInfo } = useLoginInfo();
    const [bulkUpload, setBulkUpload] = useState(null);
    const { banks, banksLoading } = useBanks(userInfo?.token);
    const { businessCreditLoading, businessCredit } = useSelector(s => s.entities.wallet)
    
    const banksData = useMemo(() => {
        return banks?.sort((a, b) => (a?.bankName || a?.institutionName)?.localeCompare(b?.bankName || b?.institutionName))?.map((singleBank) => ({
            label: singleBank?.bankName || singleBank?.institutionName,
            value: singleBank?.bankCode || singleBank?.institutionCode,
        })) || [];
    }, [banks])

    const handleBankChange = (value) => {
        const selectedBankCode = value;
        const selectedBank = banks?.filter(bank => (bank?.institutionCode || bank?.bankCode) === selectedBankCode)?.[0];
        setForm({
            ...form,
            bankCode: selectedBankCode,
            bankName: selectedBank?.institutionName || selectedBank?.bankName,
        });
    };

    const [form, setForm] = useState({
        bankName: "",
        bankCode: "",
        pdf: null,
        businessId: userInfo?.employer?._id,
        email: userInfo?.employer?.email,
        reason: "",
        annualTurnover: "",
        pdfPassword: ""
    })

    const submitForm = (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('bankName', form?.bankName);
        formData.append('bankCode', form?.bankCode);
        formData.append('pdf', bulkUpload);
        formData.append('businessId', form?.businessId);
        formData.append('email', form?.email);
        formData.append('reason', form?.reason);
        formData.append('annualTurnover', Number(form?.annualTurnover?.split(",").join("")));
        formData.append('pdfPassword', form?.pdfPassword);

        dispatch(walletActions.createBusinessCredit(formData));
    }

    const isFormNotValid = useMemo(() => {
        return !form?.bankName || !form.reason || !form.annualTurnover || !bulkUpload;
    }, [form, bulkUpload]);

    useEffect(() => {
        if (businessCredit?.status === CreditStatus.PENDING) {
            history.push('/credit/application-submitted');
        }
    }, [businessCredit, dispatch, history]);
    
    useEffect(() => {
        dispatch(banksActions.getAllBanks());
    }, [dispatch]);

    return (
      <div className="flex justify-center items-center">
        <div className="relative bg-[#FFFFFF] h-full p-[22px] rounded-lg">
            <div className="font-aeonik text-2xl font-bold leading-7 text-left my-2">
                <div>Credit Application</div>
            </div>
            <div className="font-thin text-[16px]">
                <p>Please upload the bank statement of your most active bank account</p>
            </div>

            <div className="font-aeonik mt-[48px]">
                <Input
                    type="text"
                    name="reason"
                    label="Purpose of Credit Request?"
                    placeholder="Eg. Expanding business operations, invoice financing etc."
                    value={form.reason}
                    required
                    onChange={(evt) => {
                        const { value } = evt.target;
                        if (!(/^[a-zA-Z0-9 ]*$/).test(value)) return;
                        setForm({ ...form, reason: value });
                    }}
                />
            </div>

            <div className="font-aeonik mt-[48px]">
                <Input
                    type="text"
                    name="annualTurnover"
                    label="What is your annual turnover?"
                    placeholder="Eg. N12,000,000"
                    className="input-number"
                    value={form.annualTurnover}
                    required
                    onChange={(evt) => {
                        const { value } = evt.target;
                        if (value === "0") return;
                        const cleanedValue = value.replace(/[^\d.]/g, '');
                        const [integerPart, decimalPart] = cleanedValue.split('.');
                        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                        let formattedValue = formattedInteger;
                        if (decimalPart !== undefined) {
                            formattedValue += '.' + decimalPart.slice(0, 2);
                        }
                        setForm({
                            ...form,
                            annualTurnover: formattedValue,
                        })
                    }}
                />
            </div>

            <div className="font-aeonik mt-[48px]">
                {/* <SelectInput disabled={banksLoading} label="Bank Name" value={form.bankCode} onChange={(e) => handleBankChange(e)} required>
                    <option value="">{banksLoading ? "Fetching bank lists..." : "Select Bank"}</option>
                    {banks?.sort((a, b) => (a?.bankName || a?.institutionName)?.localeCompare(b?.bankName || b?.institutionName))?.map(bank => (
                        <option key={bank?.id || bank?.bankId} value={bank?.institutionCode || bank?.bankCode}>{bank?.institutionName || bank?.bankName}</option>
                    ))}
                </SelectInput> */}
                <p className="text-sm mb-1">Bank Name <span className="form-input-required">*</span></p>
                <ReactSelect
                    isDisabled={banksLoading}
                    isLoading={banksLoading}
                    isSearchable
                    name="bank_code"
                    onChange={(selected) => {
                        handleBankChange(selected.value);
                    }}
                    options={banksData}
                    styles={reactSelectStyles}
                    placeholder={banksLoading ? "Fetching banks..." : "Select Bank"}
                    noOptionsMessage={() => "No Banks Found"}
                />
            </div>

            <form onSubmit={submitForm}>
                <div className="font-aeonik text-xl font-semibold leading-7 text-left my-4">
                    <div>Bank Statement <span className="form-input-required" style={{ fontWeight: "normal" }}>*</span></div>
                </div>

                <div className="h-[115px] border border-dashed border-gray-300 rounded-2xl overflow-hidden mb-4">
                    <Maybe condition={bulkUpload === null}>
                        <FileUploader handleChange={(e) => setBulkUpload(e)} multiple={false} types={["pdf"]} onTypeError={(err) => toast.error(err)} onSizeError={(err) => toast.error(err)}>
                            <FileUploadUI fileIcon={iconCSV} />
                        </FileUploader>
                    </Maybe>

                    <Maybe condition={bulkUpload !== null}>
                        <div className="flex w-full h-full p-6 bg-[#FBFCFF] items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="max-w-xs text-sm ellipsis">{bulkUpload?.name}</div>
                                <div className="uppercase">({getFileSize(bulkUpload?.size)})</div>
                            </div>
                            <div>
                                <RedXIcon onClick={() => setBulkUpload(null)} />
                            </div>
                        </div>
                    </Maybe>
                </div>

                <CreditWarning />
                
                <div className="font-aeonik mt-8">
                    <Input
                        type="text"
                        name="pdfPassword"
                        label="PDF Password (Optional)"
                        placeholder="Enter Password"
                        value={form.pdfPassword}
                        onChange={(evt) => {
                            const { value } = evt.target;
                            setForm({
                                ...form,
                                pdfPassword: value,
                            })
                        }}
                    />
                </div>

                <div className="mt-8 flex w-full">
                    <ButtonLoader disabled={isFormNotValid} loading={businessCreditLoading} type="submit" className="btn h-[56px] w-full btn-ep-primary">
                        Submit
                    </ButtonLoader>
                </div>
            </form>
        </div>
      </div>
    )
}

export default CreditApplication;